import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../shadcn/table";
import { Button } from "@/components/shadcn/button";
import { SearchBar } from "@/components/SearchBar";
import { PageChanger } from "@/components/PageChanger";
import { AppUsersTable } from "./AppUsersTable";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { CircleX, ExternalLink, Info } from "lucide-react";
import Link from "next/link";
import BaseDialog from "./BaseDialog";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { NoxusBanner } from "../UI/NoxusBanner";

const PAGE_SIZE = 100;

interface AddAppUsersModalProps {
  value: string[];
  onChange: (value: string[]) => void;
  isDisabled?: boolean;
}

const UsersTableInput = ({
  value,
  onChange,
  isDisabled,
}: AddAppUsersModalProps) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [userAccess, setUserAccess] = useState<Record<string, boolean>>({});
  const [isOpen, setIsOpen] = useState(false);

  const { data: usersData, isLoading } = useRemoteData(
    DATA_KEYS.USERS,
    {
      query: {
        page: currentPage,
        size: PAGE_SIZE,
        display_name: searchValue,
        email: searchValue,
        group_id: spotGroup.id,
      },
    },
    { placeholderData: keepPreviousData, staleTime: 5000 }
  );

  useEffect(() => {
    if (isOpen) {
      setUserAccess(
        value.reduce(
          (acc, userId) => {
            acc[userId] = true;
            return acc;
          },
          {} as Record<string, boolean>
        )
      );
    }
  }, [isOpen, value]);

  const handleSearchChange = (value: string) => {
    if (value !== searchValue) {
      setCurrentPage(1);
    }
    setSearchValue(value);
  };

  const allowedUsersList =
    usersData?.items?.filter((userWithGroup) =>
      value.includes(userWithGroup.user.id)
    ) || [];

  const handleSave = () => {
    onChange(Object.keys(userAccess).filter((user) => userAccess[user]));
    setIsOpen(false);
  };

  const onRemoveUser = (userId: string) => {
    onChange(value.filter((user) => user !== userId));
  };

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allowedUsersList.map((userWithGroup) => (
            <TableRow
              key={userWithGroup.user.id}
              className="hover:bg-transparent"
              data-testid="allowed-user-row"
            >
              <TableCell>{userWithGroup.user.display_name}</TableCell>
              <TableCell>{userWithGroup.user.email}</TableCell>
              <TableCell className="text-right py-3">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${userWithGroup.user.display_name}`}
                  onClick={() => {
                    onRemoveUser(userWithGroup.user.id);
                  }}
                  disabled={isDisabled}
                >
                  <CircleX
                    className="text-muted-foreground"
                    strokeWidth={1.33}
                  />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {allowedUsersList.length === 0 && (
        <div className="mt-4">
          <NoxusBanner
            icon={<Info className="mt-0.5" />}
            description="Add users to grant them access"
          />
        </div>
      )}
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="mt-4"
        disabled={isDisabled}
      >
        Add users
      </Button>
      <BaseDialog
        size="3xl"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        title="Add users"
        description="Add specific users individually to grant them access"
        footerActions={[
          {
            label: "Cancel",
            props: {
              variant: "outline",
              onClick: () => setIsOpen(false),
            },
          },
          {
            label: "Save",
            props: { onClick: handleSave, disabled: isDisabled },
          },
        ]}
        footerLeft={
          <Link
            href={`/settings/administration/workspaces/${spotGroup.id}?tab=users`}
            target="_blank"
            rel="noreferrer noopener"
          >
            <Button variant="outline">
              <ExternalLink className="w-4 h-4" />
              Manage users
            </Button>
          </Link>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <SearchBar
              value={searchValue}
              setValue={handleSearchChange}
              isDebounced
            />
          </div>

          <AppUsersTable
            data={usersData?.items || []}
            isLoading={isLoading}
            userAccess={userAccess}
            onUserAccessChange={setUserAccess}
          />

          {usersData && (usersData?.pages || 0) > 1 && (
            <div className="flex justify-center">
              <PageChanger
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={usersData.pages || 0}
                maxWidth="3.5rem"
              />
            </div>
          )}
        </div>
      </BaseDialog>
    </div>
  );
};

interface IUsersTableField {
  name: string;
  placeholder?: string;
  label?: string;
  helperText?: string;
  tooltip?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  isLoading?: boolean;
  isDisabled?: boolean;

  defaultValue?: string;
}

export const UsersTableField = ({
  name,
  placeholder,
  label,
  helperText,
  tooltip,
  rules,
  control,
  isLoading,
  isDisabled,
  defaultValue,
}: IUsersTableField) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      control={control}
      render={({ field }) => (
        <UsersTableInput
          value={field.value}
          onChange={field.onChange}
          isDisabled={isDisabled}
        />
      )}
    />
  );
};
